import * as React from 'react';

import { Button } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Link2 } from 'lucide-react';
import { PlaidLinkOnSuccessMetadata, usePlaidLink } from 'react-plaid-link';

import { useFinancialAccountSync } from '../../hooks/mutations/useFinancialAccountSync';
import { useTranslations } from '../../i18n/useTranslations';

import {
  FacilityMapping,
  PlaidAccountInfo,
  PlaidFacilityAssignmentDialog,
} from './PlaidFacilityAssignmentDialog';

import * as plaidState from '~/modules/finance/_state/plaidLinkButton.state';

type PendingPlaidData = {
  publicToken: string;
  accounts: PlaidAccountInfo[];
};

export const PlaidLinkButton = ({
  pending,
  onSuccess,
}: PlaidLinkButtonProps) => {
  const t = useTranslations();
  const { data } = useAtomValue(plaidState.$remotePlaidItems);
  const plaidItems = data?.plaidItems;
  const { success, error } = useToast();

  const [linkToken, setLinkToken] = useAtom(plaidState.$linkToken);
  const [showFacilityDialog, setShowFacilityDialog] = React.useState(false);
  const [pendingPlaidData, setPendingPlaidData] =
    React.useState<PendingPlaidData | null>(null);
  const [isConnecting, setIsConnecting] = React.useState(false);

  const generateLinkToken = useSetAtom(plaidState.$generateLinkToken);
  const { isPending: generateLinkTokenIsLoading } = useAtomValue(
    plaidState.$generateLinkToken,
  );
  const exchangePublicToken = useSetAtom(plaidState.$exchangePublicToken);
  const {
    mutateAsync: syncLinkedAccounts,
    isPending: syncLinkedAccountsIsLoading,
  } = useFinancialAccountSync();

  const handleConnectAccounts = async () => {
    if (plaidItems && plaidItems?.length > 0) {
      try {
        await syncLinkedAccounts({});
        success(t.financialAccounts.plaid.messages.syncSuccess);
        onSuccess();
      } catch (e) {
        console.error(e);
        error(t.financialAccounts.plaid.messages.syncError);
      }
    } else {
      try {
        const { integrationsPlaidCreateLinkToken } = await generateLinkToken();
        setLinkToken(integrationsPlaidCreateLinkToken.token);
      } catch (e) {
        console.error(e);
        error(t.financialAccounts.plaid.messages.initError);
      }
    }
  };

  const mapPlaidMetadataAccounts = (
    accounts: PlaidLinkOnSuccessMetadata['accounts'],
  ): PlaidAccountInfo[] =>
    accounts.map((account) => ({
      id: account.id,
      name: account.name,
      type: account.subtype ?? account.type ?? 'unknown',
      mask: account.mask,
    }));

  const { open } = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken, metadata) => {
      const accounts = mapPlaidMetadataAccounts(metadata.accounts);
      setPendingPlaidData({ publicToken, accounts });
      setShowFacilityDialog(true);
      setLinkToken(null);
    },
    onExit: (exitError, metadata) => {
      if (exitError != null) {
        console.error('Plaid error:', exitError, metadata);
      } else {
        console.log('User exited Plaid flow:', metadata);
      }

      setLinkToken(null);
    },
  });

  const handleFacilityConfirm = async (mappings: FacilityMapping[]) => {
    if (!pendingPlaidData) return;

    setIsConnecting(true);
    try {
      await exchangePublicToken({ publicToken: pendingPlaidData.publicToken });
      await syncLinkedAccounts({
        facilityMappings: mappings.map((m) => ({
          plaidAccountId: m.plaidAccountId,
          facilityID: m.facilityID,
        })),
      });
      success(t.financialAccounts.plaid.messages.connectSuccess);
      onSuccess();
    } catch (e) {
      console.error(e);
      error(t.financialAccounts.plaid.messages.connectError);
    } finally {
      setIsConnecting(false);
      setShowFacilityDialog(false);
      setPendingPlaidData(null);
    }
  };

  const handleFacilityCancel = () => {
    setShowFacilityDialog(false);
    setPendingPlaidData(null);
  };

  React.useEffect(() => {
    if (plaidItems?.length === 0 && linkToken != null) {
      open();
    }
  }, [linkToken, open, plaidItems]);

  return (
    <>
      <Button
        color="primary"
        onPress={handleConnectAccounts}
        isLoading={
          pending || generateLinkTokenIsLoading || syncLinkedAccountsIsLoading
        }
        icon={<Link2 />}
        variant="solid"
        className="@PlaidLinkButton"
      >
        {t.financialAccounts.plaid.linkButton}
      </Button>

      <PlaidFacilityAssignmentDialog
        isOpen={showFacilityDialog}
        accounts={pendingPlaidData?.accounts ?? []}
        onConfirm={handleFacilityConfirm}
        onCancel={handleFacilityCancel}
        isLoading={isConnecting}
      />
    </>
  );
};

type PlaidLinkButtonProps = {
  pending?: boolean;
  onSuccess: () => void;
};
